import { defineStore } from 'pinia'
import { ref } from 'vue'
import apiClient from '@/services/authService'

export const useExamReleaseStore = defineStore('examRelease', () => {
    // State
    const releases = ref([])
    const loading = ref(false)
    const error = ref(null)

    // Actions
    const createRelease = async (releaseData) => {
        try {
            loading.value = true
            error.value = null
            
            const response = await apiClient.post('/api/v1/exam-release/create', releaseData)
            
            if (response.data.success) {
                return response.data
            } else {
                throw new Error(response.data.message || 'Erro ao criar liberação')
            }
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            loading.value = false
        }
    }

    const searchReleases = async (filters) => {
        try {
            loading.value = true
            error.value = null
            
            const response = await apiClient.post('/api/v1/exam-release/search', filters)
            
            if (response.data.success) {
                releases.value = response.data.items || []
                return response.data
            } else {
                throw new Error(response.data.message || 'Erro ao buscar liberações')
            }
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            loading.value = false
        }
    }

    const updateRelease = async (releaseId, updateData) => {
        try {
            loading.value = true
            error.value = null
            
            const response = await apiClient.put(`/api/v1/exam-release/${releaseId}/`, updateData)
            
            if (response.data.success) {
                return response.data
            } else {
                throw new Error(response.data.message || 'Erro ao atualizar liberação')
            }
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            loading.value = false
        }
    }

    const revokeRelease = async (releaseId) => {
        try {
            loading.value = true
            error.value = null
            
            const response = await apiClient.post(`/api/v1/exam-release/${releaseId}/revoke`)
            
            if (response.data.success) {
                return response.data
            } else {
                throw new Error(response.data.message || 'Erro ao revogar liberação')
            }
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            loading.value = false
        }
    }

    const syncRelease = async (releaseId) => {
        try {
            loading.value = true
            error.value = null
            
            const response = await apiClient.post(`/api/v1/exam-release/${releaseId}/sync`)
            
            if (response.data.success) {
                return response.data
            } else {
                throw new Error(response.data.message || 'Erro ao sincronizar liberação')
            }
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            loading.value = false
        }
    }

    const getConfig = async () => {
        try {
            loading.value = true
            error.value = null
            
            const response = await apiClient.get('/api/v1/exam-release/config')
            
            if (response.data.success) {
                return response.data.config
            } else {
                throw new Error(response.data.message || 'Erro ao obter configurações')
            }
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            loading.value = false
        }
    }

    const updateConfig = async (configData) => {
        try {
            loading.value = true
            error.value = null
            
            const response = await apiClient.put('/api/v1/exam-release/config', configData)
            
            if (response.data.success) {
                return response.data
            } else {
                throw new Error(response.data.message || 'Erro ao atualizar configurações')
            }
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            loading.value = false
        }
    }

    const releaseToWebManually = async (releaseId, data) => {
        try {
            loading.value = true
            error.value = null
            
            const response = await apiClient.post(`/api/v1/exam-release/${releaseId}/release-web`, data)
            
            if (response.data.success) {
                return response.data
            } else {
                throw new Error(response.data.message || 'Erro ao liberar para web')
            }
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            loading.value = false
        }
    }

    const releaseToWebDirect = async (releaseId) => {
        try {
            loading.value = true
            error.value = null
            
            const response = await apiClient.post(`/api/v1/exam-release/${releaseId}/release-web-direct`)
            
            if (response.data.success) {
                return response.data
            } else {
                throw new Error(response.data.message || 'Erro ao liberar para web')
            }
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            loading.value = false
        }
    }

    const releaseMedicalReportToWebDirect = async (medicalReportId) => {
        try {
            loading.value = true
            error.value = null
            
            const response = await apiClient.post(`/api/v1/exam-release/medical-report/${medicalReportId}/release-web-direct`)
            
            if (response.data.success) {
                return response.data
            } else {
                throw new Error(response.data.message || 'Erro ao liberar laudo para web')
            }
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            loading.value = false
        }
    }

    const revokeWebAccess = async (releaseId, data) => {
        try {
            loading.value = true
            error.value = null
            
            const response = await apiClient.post(`/api/v1/exam-release/${releaseId}/revoke-web`, data)
            
            if (response.data.success) {
                return response.data
            } else {
                throw new Error(response.data.message || 'Erro ao revogar acesso web')
            }
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            loading.value = false
        }
    }

    const getWebAccessInfo = async (releaseId) => {
        try {
            loading.value = true
            error.value = null
            
            const response = await apiClient.get(`/api/v1/exam-release/${releaseId}/web-info`)
            
            if (response.data.success) {
                return response.data.web_info
            } else {
                throw new Error(response.data.message || 'Erro ao obter informações web')
            }
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            loading.value = false
        }
    }

    const getPatients = async () => {
        try {
            loading.value = true
            error.value = null
            
            const response = await apiClient.get('/api/v1/exam-release/patients')
            
            if (response.data.success) {
                return response.data.patients
            } else {
                throw new Error(response.data.message || 'Erro ao buscar pacientes')
            }
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            loading.value = false
        }
    }

    const getMedicalReports = async () => {
        try {
            loading.value = true
            error.value = null
            
            const response = await apiClient.get('/api/v1/exam-release/medical-reports')
            
            if (response.data.success) {
                return response.data.medical_reports
            } else {
                throw new Error(response.data.message || 'Erro ao buscar laudos médicos')
            }
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            loading.value = false
        }
    }

    const getStudies = async () => {
        try {
            loading.value = true
            error.value = null
            
            const response = await apiClient.get('/api/v1/exam-release/studies')
            
            if (response.data.success) {
                return response.data.studies
            } else {
                throw new Error(response.data.message || 'Erro ao buscar estudos')
            }
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            loading.value = false
        }
    }

    const clearError = () => {
        error.value = null
    }

    return {
        // State
        releases,
        loading,
        error,
        
        // Actions
        createRelease,
        searchReleases,
        updateRelease,
        revokeRelease,
        syncRelease,
        releaseToWebManually,
        releaseToWebDirect,
        releaseMedicalReportToWebDirect,
        revokeWebAccess,
        getWebAccessInfo,
        getPatients,
        getMedicalReports,
        getStudies,
        getConfig,
        updateConfig,
        clearError
    }
}) 